import React, { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { 
  Plus, Search, Calendar as CalendarIcon, Send, Check, AlertTriangle, Trash2, 
  RefreshCw, Eye, Ban, Download, Pencil, Mail, Type, Package, X,
  ArrowUpDown, ArrowUp, ArrowDown, FileText, DollarSign, Clock, TrendingUp
} from "lucide-react";
import axios from "axios";
import { format, addDays } from "date-fns";
import { sv } from "date-fns/locale";
import { CustomerSelector } from "@/components/CustomerSelector";
import { InvoicePreview } from "@/components/InvoicePreview";
import { PreviewModal } from "@/components/PreviewModal";

const SalesInvoices = () => {
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [company, setCompany] = useState({});
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [emailForm, setEmailForm] = useState({ recipient_email: "", subject: "", message: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Multi-select
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  
  // Sorting
  const [sortColumn, setSortColumn] = useState("invoice_date");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    invoice_number: "",
    customer_name: "",
  });
  
  const getInitialFormData = () => ({
    customer_id: "",
    invoice_date: format(new Date(), "yyyy-MM-dd"),
    due_date: format(addDays(new Date(), 30), "yyyy-MM-dd"),
    our_reference: user?.name || "",
    your_reference: "",
    lines: [{ description: "", quantity: 1, unit: "st", unit_price: 0, vat_rate: 25, discount_percent: 0 }],
    notes: "",
    is_recurring: false,
    recurring_interval: null,
    recurring_end_date: null
  });
  
  const [formData, setFormData] = useState(getInitialFormData());

  useEffect(() => {
    fetchData();
  }, []);
  
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && invoices.length > 0 && !loading) {
      const invoiceToEdit = invoices.find(inv => inv.id === editId);
      if (invoiceToEdit) {
        handleEditInvoice(invoiceToEdit);
        setSearchParams({});
      }
    }
  }, [searchParams, invoices, loading]);

  const fetchData = async () => {
    try {
      const [invoicesRes, customersRes, articlesRes, companyRes, emailStatusRes] = await Promise.all([
        axios.get(`${API}/sales-invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/articles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/company`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/email/status`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { configured: false } }))
      ]);
      setInvoices(invoicesRes.data);
      setCustomers(customersRes.data);
      setArticles(articlesRes.data);
      if (companyRes.data) setCompany(companyRes.data);
      setEmailConfigured(emailStatusRes.data?.configured || false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Kunde inte hämta data");
    } finally {
      setLoading(false);
    }
  };

  // Sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <ArrowUpDown size={14} className="ml-1 text-slate-400" />;
    return sortDirection === "asc" 
      ? <ArrowUp size={14} className="ml-1 text-indigo-600" />
      : <ArrowDown size={14} className="ml-1 text-indigo-600" />;
  };

  // Selection
  const toggleSelectAll = () => {
    if (selectedInvoices.size === filteredInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(filteredInvoices.map(i => i.id)));
    }
  };

  const toggleInvoice = (id) => {
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedInvoices(newSelected);
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (selectedInvoices.size === 0) return;
    
    let deleted = 0;
    let failed = 0;
    
    for (const id of selectedInvoices) {
      const invoice = invoices.find(i => i.id === id);
      if (invoice?.status !== "draft") {
        failed++;
        continue;
      }
      try {
        await axios.delete(`${API}/sales-invoices/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deleted++;
      } catch {
        failed++;
      }
    }
    
    if (deleted > 0) toast.success(`${deleted} fakturor borttagna`);
    if (failed > 0) toast.error(`${failed} kunde inte tas bort (endast utkast kan raderas)`);
    
    setSelectedInvoices(new Set());
    fetchData();
  };

  const handleBulkMarkPaid = async () => {
    if (selectedInvoices.size === 0) return;
    
    let updated = 0;
    let failed = 0;
    
    for (const id of selectedInvoices) {
      const invoice = invoices.find(i => i.id === id);
      if (!["sent", "overdue"].includes(invoice?.status)) {
        failed++;
        continue;
      }
      try {
        const formData = new FormData();
        formData.append("status", "paid");
        await axios.put(`${API}/sales-invoices/${id}/status`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        updated++;
      } catch {
        failed++;
      }
    }
    
    if (updated > 0) toast.success(`${updated} fakturor markerade som betalda`);
    if (failed > 0) toast.error(`${failed} kunde inte uppdateras`);
    
    setSelectedInvoices(new Set());
    fetchData();
  };

  const handleBulkMarkSent = async () => {
    if (selectedInvoices.size === 0) return;
    
    let updated = 0;
    let failed = 0;
    
    for (const id of selectedInvoices) {
      const invoice = invoices.find(i => i.id === id);
      if (invoice?.status !== "draft") {
        failed++;
        continue;
      }
      try {
        const formData = new FormData();
        formData.append("status", "sent");
        await axios.put(`${API}/sales-invoices/${id}/status`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        updated++;
      } catch {
        failed++;
      }
    }
    
    if (updated > 0) toast.success(`${updated} fakturor markerade som skickade`);
    if (failed > 0) toast.error(`${failed} kunde inte uppdateras`);
    
    setSelectedInvoices(new Set());
    fetchData();
  };

  const openEmailDialog = (invoice) => {
    const customer = customers.find(c => c.id === invoice.customer_id);
    setEmailInvoice(invoice);
    setEmailForm({
      recipient_email: customer?.email || "",
      subject: `Faktura ${invoice.invoice_number} från ${company?.name || "oss"}`,
      message: ""
    });
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailInvoice) return;
    
    setSendingEmail(true);
    try {
      await axios.post(
        `${API}/sales-invoices/${emailInvoice.id}/send-email`,
        emailForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('invoices.emailSent'));
      setEmailDialogOpen(false);
      setEmailInvoice(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invoices.emailFailed'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleAddLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { description: "", quantity: 1, unit: "st", unit_price: 0, vat_rate: 25, discount_percent: 0, is_freetext: false }]
    });
  };

  const handleRemoveLine = (index) => {
    if (formData.lines.length <= 1) return;
    setFormData({
      ...formData,
      lines: formData.lines.filter((_, i) => i !== index)
    });
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    setFormData({ ...formData, lines: newLines });
  };

  const handleToggleLineType = (index) => {
    const newLines = [...formData.lines];
    newLines[index].is_freetext = !newLines[index].is_freetext;
    if (newLines[index].is_freetext) {
      newLines[index].article_id = null;
    }
    setFormData({ ...formData, lines: newLines });
  };

  const handleArticleSelect = (index, articleId) => {
    const article = articles.find(a => a.id === articleId);
    if (article) {
      const newLines = [...formData.lines];
      newLines[index] = {
        ...newLines[index],
        article_id: articleId,
        description: article.name,
        unit: article.unit,
        unit_price: article.price,
        vat_rate: article.vat_rate
      };
      setFormData({ ...formData, lines: newLines });
    }
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setFormData({
        ...formData,
        customer_id: customerId,
        customer_name: customer.name,
        due_date: format(addDays(new Date(formData.invoice_date), customer.payment_terms || 30), "yyyy-MM-dd")
      });
    } else {
      setFormData({ ...formData, customer_id: customerId, customer_name: "" });
    }
  };

  const handleCustomerCreated = (newCustomer) => {
    setCustomers(prev => [...prev, newCustomer]);
    setFormData(prev => ({
      ...prev,
      customer_id: newCustomer.id,
      customer_name: newCustomer.name
    }));
  };

  const resetForm = () => {
    setFormData(getInitialFormData());
    setEditingInvoice(null);
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      customer_id: invoice.customer_id || "",
      invoice_date: invoice.invoice_date || format(new Date(), "yyyy-MM-dd"),
      due_date: invoice.due_date || format(addDays(new Date(), 30), "yyyy-MM-dd"),
      our_reference: invoice.our_reference || "",
      your_reference: invoice.your_reference || "",
      lines: invoice.lines?.length > 0 ? invoice.lines.map(line => ({
        article_id: line.article_id || null,
        description: line.description || "",
        quantity: line.quantity || 1,
        unit: line.unit || "st",
        unit_price: line.unit_price || 0,
        vat_rate: line.vat_rate || 25,
        discount_percent: line.discount_percent || 0
      })) : [{ description: "", quantity: 1, unit: "st", unit_price: 0, vat_rate: 25, discount_percent: 0 }],
      notes: invoice.notes || "",
      is_recurring: invoice.is_recurring || false,
      recurring_interval: invoice.recurring_interval || null,
      recurring_end_date: invoice.recurring_end_date || null
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInvoice) {
        await axios.put(`${API}/sales-invoices/${editingInvoice.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Faktura uppdaterad");
      } else {
        await axios.post(`${API}/sales-invoices`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Faktura skapad");
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte spara faktura");
    }
  };

  const handleStatusChange = async (invoiceId, status) => {
    try {
      const formData = new FormData();
      formData.append("status", status);
      await axios.put(`${API}/sales-invoices/${invoiceId}/status`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status uppdaterad");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte uppdatera status");
    }
  };

  const handleSendReminder = async (invoiceId, includeFee) => {
    try {
      await axios.post(`${API}/sales-invoices/${invoiceId}/send-reminder?include_fee=${includeFee}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(includeFee ? "Påminnelse skickad med avgift" : "Påminnelse skickad");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte skicka påminnelse");
    }
  };

  const handleSendToCollection = async (invoiceId) => {
    try {
      await axios.post(`${API}/sales-invoices/${invoiceId}/send-to-collection`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Skickad till inkasso.ai");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte skicka till inkasso");
    }
  };

  const handleDelete = async (invoiceId) => {
    try {
      await axios.delete(`${API}/sales-invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Faktura borttagen");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ta bort faktura");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: <Badge className="badge-pending">{t('invoices.status.draft')}</Badge>,
      sent: <Badge className="bg-blue-100 text-blue-700">{t('invoices.status.sent')}</Badge>,
      paid: <Badge className="badge-success">{t('invoices.status.paid')}</Badge>,
      overdue: <Badge className="badge-error">{t('invoices.status.overdue')}</Badge>,
      cancelled: <Badge className="bg-slate-100 text-slate-700">{t('invoices.status.cancelled')}</Badge>,
      collection: <Badge className="bg-purple-100 text-purple-700">{t('nav.inkasso')}</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const calculateTotal = () => {
    let subtotal = 0;
    let vat = 0;
    formData.lines.forEach(line => {
      const lineTotal = line.quantity * line.unit_price * (1 - (line.discount_percent || 0) / 100);
      subtotal += lineTotal;
      vat += lineTotal * (line.vat_rate / 100);
    });
    return { subtotal, vat, total: subtotal + vat };
  };

  const totals = calculateTotal();

  // Filtering and sorting
  const filteredInvoices = useMemo(() => {
    return invoices
      .filter(inv => {
        const matchesSearch = !searchTerm ||
          inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = filterStatus === "all" || inv.status === filterStatus;
        
        const matchesInvoiceNumber = !columnFilters.invoice_number ||
          inv.invoice_number?.toLowerCase().includes(columnFilters.invoice_number.toLowerCase());
        const matchesCustomer = !columnFilters.customer_name ||
          inv.customer_name?.toLowerCase().includes(columnFilters.customer_name.toLowerCase());
        
        return matchesSearch && matchesStatus && matchesInvoiceNumber && matchesCustomer;
      })
      .sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];
        
        if (sortColumn === "total") {
          aVal = a.total || 0;
          bVal = b.total || 0;
        } else if (typeof aVal === 'string') {
          aVal = aVal?.toLowerCase() || "";
          bVal = bVal?.toLowerCase() || "";
        }
        
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
  }, [invoices, searchTerm, filterStatus, columnFilters, sortColumn, sortDirection]);

  // Statistics
  const stats = useMemo(() => {
    const total = invoices.reduce((s, i) => s + (i.total || 0), 0);
    const unpaid = invoices.filter(i => ["sent", "overdue"].includes(i.status)).reduce((s, i) => s + (i.total || 0), 0);
    const overdueCount = invoices.filter(i => i.status === "overdue").length;
    const paidCount = invoices.filter(i => i.status === "paid").length;
    const draftCount = invoices.filter(i => i.status === "draft").length;
    
    return { total, unpaid, overdueCount, paidCount, draftCount };
  }, [invoices]);

  // Export
  const exportInvoices = () => {
    const csv = [
      ["Fakturanr", "Kund", "Fakturadatum", "Förfallodatum", "Belopp", "Status"].join(";"),
      ...filteredInvoices.map(i => [
        i.invoice_number,
        i.customer_name,
        i.invoice_date,
        i.due_date,
        i.total,
        i.status
      ].join(";"))
    ].join("\n");
    
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fakturor_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export klar!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="sales-invoices">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('invoices.title')}</h1>
          <p className="text-slate-600">Hantera alla dina försäljningsfakturor</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportInvoices}>
            <Download size={16} className="mr-2" />
            Exportera
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-invoice-btn">
                <Plus size={16} className="mr-2" />
                {t('invoices.newInvoice')}
              </Button>
            </DialogTrigger>
            <DialogContent size="full" className="max-w-[95vw] max-h-[95vh] p-0">
              <div className="flex h-[95vh]">
                {/* Left side - Form */}
                <div className="w-1/2 p-6 overflow-y-auto border-r border-slate-200">
                  <DialogHeader className="mb-4">
                    <DialogTitle>{editingInvoice ? `${t('invoices.editInvoice')} ${editingInvoice.invoice_number || ''}` : t('invoices.createInvoice')}</DialogTitle>
                    <DialogDescription>{t('invoices.subtitle')}</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('invoices.customer')} *</Label>
                        <CustomerSelector
                          customers={customers}
                          value={formData.customer_id}
                          onChange={handleCustomerSelect}
                          onCustomerCreated={handleCustomerCreated}
                          placeholder={t('customers.newCustomer') + '...'}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                          <Label>{t('invoices.invoiceDate')}</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {formData.invoice_date}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={new Date(formData.invoice_date)}
                                onSelect={(date) => date && setFormData({ ...formData, invoice_date: format(date, "yyyy-MM-dd") })}
                                locale={sv}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label>{t('invoices.dueDate')}</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {formData.due_date}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={new Date(formData.due_date)}
                                onSelect={(date) => date && setFormData({ ...formData, due_date: format(date, "yyyy-MM-dd") })}
                                locale={sv}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('invoices.ourReference')}</Label>
                        <Input
                          value={formData.our_reference}
                          onChange={(e) => setFormData({ ...formData, our_reference: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('invoices.yourReference')}</Label>
                        <Input
                          value={formData.your_reference}
                          onChange={(e) => setFormData({ ...formData, your_reference: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Recurring settings */}
                    <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.is_recurring}
                          onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm font-medium">{t('invoices.recurring')}</span>
                      </label>
                      {formData.is_recurring && (
                        <Select
                          value={formData.recurring_interval || ""}
                          onValueChange={(value) => setFormData({ ...formData, recurring_interval: value })}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder={t('invoices.recurringInterval')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">{t('invoices.monthly')}</SelectItem>
                            <SelectItem value="quarterly">{t('invoices.quarterly')}</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Invoice lines */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label>{t('invoices.lines')}</Label>
                        <Button type="button" variant="outline" size="sm" onClick={handleAddLine}>
                          <Plus size={14} className="mr-1" />
                          {t('invoices.addLine')}
                        </Button>
                      </div>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left p-2 w-10">Typ</th>
                              <th className="text-left p-2 w-24">{t('invoices.article')}</th>
                              <th className="text-left p-2">{t('common.description')}</th>
                              <th className="text-right p-2 w-20">{t('invoices.quantity')}</th>
                              <th className="text-right p-2 w-24">{t('invoices.unitPrice')}</th>
                              <th className="text-right p-2 w-20">{t('invoices.vat')}</th>
                              <th className="text-right p-2 w-24">{t('common.total')}</th>
                              <th className="w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.lines.map((line, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="p-2">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleLineType(idx)}
                                    className={`h-8 w-8 p-0 ${line.is_freetext ? 'text-blue-600 bg-blue-50' : 'text-indigo-600 bg-indigo-50'}`}
                                    title={line.is_freetext ? 'Fritext (klicka för artikel)' : 'Artikel (klicka för fritext)'}
                                  >
                                    {line.is_freetext ? <Type size={14} /> : <Package size={14} />}
                                  </Button>
                                </td>
                                <td className="p-2">
                                  {!line.is_freetext ? (
                                    <Select onValueChange={(v) => handleArticleSelect(idx, v)} value={line.article_id || ""}>
                                      <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder="Välj..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {articles.map(a => (
                                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <span className="text-xs text-slate-400 italic">Fritext</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {line.is_freetext ? (
                                    <textarea
                                      value={line.description}
                                      onChange={(e) => handleLineChange(idx, "description", e.target.value)}
                                      className="w-full h-16 text-sm border rounded-md p-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      placeholder="Skriv valfri text..."
                                    />
                                  ) : (
                                    <Input
                                      value={line.description}
                                      onChange={(e) => handleLineChange(idx, "description", e.target.value)}
                                      className="h-8"
                                    />
                                  )}
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    value={line.quantity}
                                    onChange={(e) => handleLineChange(idx, "quantity", parseFloat(e.target.value) || 0)}
                                    className="h-8 text-right"
                                  />
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    value={line.unit_price}
                                    onChange={(e) => handleLineChange(idx, "unit_price", parseFloat(e.target.value) || 0)}
                                    className="h-8 text-right"
                                  />
                                </td>
                                <td className="p-2">
                                  <Select
                                    value={line.vat_rate.toString()}
                                    onValueChange={(v) => handleLineChange(idx, "vat_rate", parseInt(v))}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="25">25%</SelectItem>
                                      <SelectItem value="12">12%</SelectItem>
                                      <SelectItem value="6">6%</SelectItem>
                                      <SelectItem value="0">0%</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="p-2 text-right font-mono">
                                  {formatCurrency(line.quantity * line.unit_price)}
                                </td>
                                <td className="p-2">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveLine(idx)}
                                    className="text-red-500"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-slate-50 font-medium">
                            <tr className="border-t">
                              <td colSpan="6" className="p-2 text-right">{t('invoices.subtotal')}:</td>
                              <td className="p-2 text-right font-mono">{formatCurrency(totals.subtotal)}</td>
                              <td></td>
                            </tr>
                            <tr>
                              <td colSpan="6" className="p-2 text-right">{t('invoices.vat')}:</td>
                              <td className="p-2 text-right font-mono">{formatCurrency(totals.vat)}</td>
                              <td></td>
                            </tr>
                            <tr className="text-lg">
                              <td colSpan="6" className="p-2 text-right font-bold">{t('common.total')}:</td>
                              <td className="p-2 text-right font-mono font-bold">{formatCurrency(totals.total)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('invoices.notes')}</Label>
                      <Input
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder={t('invoices.notes')}
                      />
                    </div>

                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                        {t('common.cancel')}
                      </Button>
                      <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={!formData.customer_id}>
                        {editingInvoice ? t('common.save') : t('invoices.createInvoice')}
                      </Button>
                    </DialogFooter>
                  </form>
                </div>
                
                {/* Right side - Live Preview */}
                <div className="w-1/2 bg-slate-100 p-6 overflow-y-auto">
                  <div className="sticky top-0">
                    <h3 className="text-sm font-semibold text-slate-600 mb-4 flex items-center gap-2">
                      <Eye size={16} />
                      Live-förhandsgranskning
                    </h3>
                    <InvoicePreview 
                      invoice={{
                        ...formData,
                        invoice_number: editingInvoice?.invoice_number || "UTKAST"
                      }} 
                      company={company}
                      type="invoice"
                      scale={0.65}
                    />
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <FileText className="text-indigo-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{invoices.length}</p>
                <p className="text-xs text-slate-500">Totalt fakturor</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-lg font-bold text-green-600">{formatCurrency(stats.total)}</p>
                <p className="text-xs text-slate-500">Totalt fakturerat</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-lg font-bold text-amber-600">{formatCurrency(stats.unpaid)}</p>
                <p className="text-xs text-slate-500">Obetalt</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={16} />
              <div>
                <p className="text-xl font-bold text-red-600">{stats.overdueCount}</p>
                <p className="text-xs text-slate-500">Förfallna</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Check className="text-green-600" size={16} />
              <div>
                <p className="text-xl font-bold text-green-600">{stats.paidCount}</p>
                <p className="text-xs text-slate-500">Betalda</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions Bar */}
      {selectedInvoices.size > 0 && (
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-indigo-900">
                {selectedInvoices.size} fakturor valda
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedInvoices(new Set())}>
                  Avmarkera
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleBulkMarkSent}
                  className="text-blue-600 border-blue-300"
                >
                  <Send size={14} className="mr-1" />
                  Markera skickade
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleBulkMarkPaid}
                  className="text-green-600 border-green-300"
                >
                  <Check size={14} className="mr-1" />
                  Markera betalda
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-600 border-red-300"
                  onClick={handleBulkDelete}
                >
                  <Trash2 size={14} className="mr-1" />
                  Ta bort valda
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters & Search */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input 
                placeholder="Sök fakturanr, kund..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla status</SelectItem>
                <SelectItem value="draft">Utkast</SelectItem>
                <SelectItem value="sent">Skickade</SelectItem>
                <SelectItem value="paid">Betalda</SelectItem>
                <SelectItem value="overdue">Förfallna</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setSearchTerm("");
                setFilterStatus("all");
                setColumnFilters({ invoice_number: "", customer_name: "" });
              }}
            >
              <X size={14} className="mr-1" />
              Rensa filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span>Fakturalista ({filteredInvoices.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredInvoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="p-3 w-10">
                      <Checkbox 
                        checked={selectedInvoices.size === filteredInvoices.length && filteredInvoices.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("invoice_number")}>
                        Fakturanr {getSortIcon("invoice_number")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("customer_name")}>
                        Kund {getSortIcon("customer_name")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("invoice_date")}>
                        Datum {getSortIcon("invoice_date")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("due_date")}>
                        Förfallodatum {getSortIcon("due_date")}
                      </button>
                    </th>
                    <th className="text-right p-3">
                      <button className="flex items-center justify-end hover:text-indigo-600 font-medium w-full" onClick={() => handleSort("total")}>
                        Belopp {getSortIcon("total")}
                      </button>
                    </th>
                    <th className="text-center p-3">
                      <button className="flex items-center justify-center hover:text-indigo-600 font-medium w-full" onClick={() => handleSort("status")}>
                        Status {getSortIcon("status")}
                      </button>
                    </th>
                    <th className="text-right p-3 font-medium">Åtgärder</th>
                  </tr>
                  {/* Column Filters */}
                  <tr className="bg-slate-100/50">
                    <th className="p-1"></th>
                    <th className="p-1">
                      <Input 
                        placeholder="Sök..." 
                        value={columnFilters.invoice_number}
                        onChange={(e) => setColumnFilters({...columnFilters, invoice_number: e.target.value})}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1">
                      <Input 
                        placeholder="Sök..." 
                        value={columnFilters.customer_name}
                        onChange={(e) => setColumnFilters({...columnFilters, customer_name: e.target.value})}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => {
                    const formattedNumber = invoice.invoice_number || "-";
                    return (
                      <tr 
                        key={invoice.id} 
                        className={`border-b border-slate-100 hover:bg-slate-50 ${selectedInvoices.has(invoice.id) ? 'bg-indigo-50' : ''}`}
                      >
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox 
                            checked={selectedInvoices.has(invoice.id)}
                            onCheckedChange={() => toggleInvoice(invoice.id)}
                          />
                        </td>
                        <td className="p-3 font-mono font-medium text-indigo-600">{formattedNumber}</td>
                        <td className="p-3">{invoice.customer_name || "-"}</td>
                        <td className="p-3 text-slate-600">{invoice.invoice_date}</td>
                        <td className="p-3 text-slate-600">{invoice.due_date}</td>
                        <td className="p-3 text-right font-medium">{formatCurrency(invoice.total)}</td>
                        <td className="p-3 text-center">
                          {getStatusBadge(invoice.status)}
                          {invoice.is_recurring && (
                            <RefreshCw size={12} className="inline ml-1 text-indigo-600" title="Löpande" />
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-1">
                            {invoice.status === "draft" && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleEditInvoice(invoice)} title="Redigera">
                                  <Pencil size={14} />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleStatusChange(invoice.id, "sent")} title="Markera som skickad">
                                  <Send size={14} />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(invoice.id)} className="text-red-500">
                                  <Trash2 size={14} />
                                </Button>
                              </>
                            )}
                            {invoice.status === "sent" && (
                              <Button variant="ghost" size="sm" onClick={() => handleStatusChange(invoice.id, "paid")} className="text-green-600" title="Markera som betald">
                                <Check size={14} />
                              </Button>
                            )}
                            {invoice.status === "overdue" && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleSendReminder(invoice.id, false)} title="Skicka påminnelse">
                                  <AlertTriangle size={14} />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleSendToCollection(invoice.id)} className="text-purple-600" title="Skicka till inkasso">
                                  <Ban size={14} />
                                </Button>
                              </>
                            )}
                            {["sent", "overdue"].includes(invoice.status) && (
                              <Button variant="ghost" size="sm" onClick={() => handleStatusChange(invoice.id, "paid")} className="text-green-600">
                                <Check size={14} />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => setPreviewInvoice(invoice)}
                              title="Förhandsgranska"
                            >
                              <Eye size={14} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => window.open(`${API}/sales-invoices/${invoice.id}/pdf`, '_blank')}
                              title="Ladda ner PDF"
                              className="text-indigo-600"
                            >
                              <Download size={14} />
                            </Button>
                            {emailConfigured && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openEmailDialog(invoice)}
                                title="Skicka e-post"
                                className="text-green-600"
                              >
                                <Mail size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 text-slate-500">
              <FileText className="mx-auto mb-4 text-slate-300" size={48} />
              <p>Inga fakturor hittades</p>
              <p className="text-sm mt-1">Skapa din första faktura för att komma igång</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('invoices.sendEmail')}</DialogTitle>
            <DialogDescription>
              {t('invoices.sendEmailDescription')} {emailInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('common.email')}</Label>
              <Input
                type="email"
                value={emailForm.recipient_email}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                placeholder="kund@foretag.se"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('invoices.emailSubject')}</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('invoices.emailMessage')}</Label>
              <Textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                placeholder={t('invoices.emailMessagePlaceholder')}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSendEmail} 
              disabled={sendingEmail || !emailForm.recipient_email}
              className="bg-green-600 hover:bg-green-700"
            >
              {sendingEmail ? (
                <>
                  <RefreshCw size={14} className="mr-2 animate-spin" />
                  {t('invoices.sending')}...
                </>
              ) : (
                <>
                  <Mail size={14} className="mr-2" />
                  {t('invoices.sendEmail')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <PreviewModal
        open={!!previewInvoice}
        onClose={() => setPreviewInvoice(null)}
        invoice={previewInvoice || {}}
        company={company}
        type="invoice"
      />
    </div>
  );
};

export default SalesInvoices;
